import { useCallback, useEffect, useRef } from 'react';

import { payments } from '@webclient/lib/agent';

import { queryClient } from '@webclient/providers/ReactQueryProvider';

import useAppSelector from '@webclient/hooks/useAppSelector';
import useQueryAfterWorkspaceLoaded from '@webclient/hooks/useQueryAfterWorkspaceLoaded';
import useSearchParamsState from '@webclient/hooks/useSearchParamsState';
import useStateUpdates from '@webclient/hooks/useStateUpdates';

import {
  CreditPackPlanType,
  CreditUsageResponseType,
  PlanKindType,
  PlanType,
  StoragePlanType,
  SubscriptionPlanType,
  WorkspacePlanType,
} from '@webclient/shared-types/agent';

import sendCreditsUsedToAnalytics from '@webclient/analytics/sendCreditsUsedToAnalytics';
import { differenceInCalendarMonths, parseISO } from 'date-fns';

export type UserFunnelType =
  | 'pack'
  | 'geniusSearch'
  | 'geniusTeams'
  | 'visitor' //? free plan but not logged in
  | 'free' //? free plan but logged in
  | null;

type CreditsUsageType = {
  general?: {
    creditPack?: CreditPackPlanType & {
      creditAsPercentage: number;
    };
    subscription?: SubscriptionPlanType & {
      creditAsPercentage: number;
    };
  };
  storage?: StoragePlanType & {
    knowledgeCapacityPercentage: number;
  };
  users?: number;
  workspace?: WorkspacePlanType;
};

const GENIUS_SEARCH_PLAN_MONTHLY = 'geniusSearchPlanMonthly';
const GENIUS_SEARCH_PLAN_YEARLY = 'geniusSearchPlanYearly';
const GENIUS_TEAMS_PLAN_MONTHLY = 'geniusTeamsPlanMonthly';
const GENIUS_TEAMS_PLAN_YEARLY = 'geniusTeamsPlanYearly';

export default function useGetCredit() {
  const [, setSearchParams] = useSearchParamsState('action', '');
  //? This is a reference to the previous credit amount, so we make sure not to send the same amount to analytics multiple times
  const previousCreditRef = useRef<number | null>(null);
  const me = useAppSelector((state) => state.auth.me);
  const { handleSubscribe } = useStateUpdates();

  useEffect(() => {
    const unsubscribe = handleSubscribe({
      type: 'credits',
      callback: (message) => {
        const { expireDate, fillDate, initialQuantity, quantity, type } =
          message.data as PlanType;

        const prev = queryClient.getQueryData(['credits']) as CreditsUsageType;

        if (prev) {
          const updatedCreditsData = {
            [type]: {
              expireDate,
              fillDate,
              initialQuantity,
              quantity,
              type,
              ...(type === 'general' && {
                creditAsPercentage: convertToPercentage({
                  quantity,
                  initialQuantity,
                }),
              }),
              ...(type === 'storage' && {
                knowledgeCapacityPercentage: convertToPercentage({
                  quantity,
                  initialQuantity,
                }),
              }),
            },
          };

          queryClient.setQueryData<CreditsUsageType>(['credits'], {
            ...prev,
            ...updatedCreditsData,
          });
        }
      },
    });

    return unsubscribe;
  }, [handleSubscribe]);

  const {
    data: creditsData,
    isLoading: isCreditLoading,
    isError: isCreditError,
  } = useQueryAfterWorkspaceLoaded({
    queryKey: ['credits'],
    queryFn: async () => {
      try {
        const response = await payments.getCredit();
        const data = response.data[0];
        const { general, storage, users, workspace } = getUserPlanDetails(data);

        const usedCredits = general.initialQuantity - general.quantity;

        const totalCredits = general.initialQuantity;

        const creditAsPercentage = convertToPercentage({
          quantity: general.quantity,
          initialQuantity: general.initialQuantity,
        });

        const knowledgeCapacityPercentage = convertToPercentage({
          quantity: storage.quantity,
          initialQuantity: storage.initialQuantity,
        });

        if (
          previousCreditRef.current === null ||
          previousCreditRef.current !== usedCredits
        ) {
          previousCreditRef.current = usedCredits;

          sendCreditsUsedToAnalytics({
            usedAmount: usedCredits,
            totalAmount: totalCredits,
            userUUID: me!.uuid,
          });
        }

        if (creditAsPercentage <= 0) {
          setSearchParams('upgrade-account');
        }

        return {
          general: {
            ...general,
            creditAsPercentage,
          },
          storage: {
            ...storage,
            knowledgeCapacityPercentage,
          },
          users,
          workspace,
        };
      } catch (error) {
        console.error(error);
        return {
          general: null,
          storage: null,
          users: 0,
          workspace: null,
        };
      }
    },
  });

  const getAccountType = useCallback(() => {
    if (creditsData == null) return null;

    const { general, workspace } = creditsData;
    if (!general?.expireDate) {
      return 'pack';
    }

    const planKind = determinPlanKind({
      plan: general,
      workspaceInitialQuantity: workspace?.initialQuantity ?? null,
    });

    if (
      planKind === 'geniusSearchPlanMonthly' ||
      planKind === 'geniusSearchPlanYearly'
    )
      return 'geniusSearch';

    if (
      planKind === 'geniusTeamsPlanMonthly' ||
      planKind === 'geniusTeamsPlanYearly'
    )
      return 'geniusTeams';

    if (me?.auth_type !== 'web_fingerprint') return 'free';

    return 'visitor';
  }, [creditsData, me?.auth_type]);

  return { creditsData, isCreditLoading, isCreditError, getAccountType };
}

function getUserPlanDetails(plans: CreditUsageResponseType[number]) {
  const workspacePlan = plans.find((plan) => plan.type === 'workspace');
  const generalPlans = plans.filter((plan) => plan.type === 'general');
  const storagePlan = plans.find((plan) => plan.type === 'storage');

  if (storagePlan == null) {
    throw new Error('Storage plan is not found');
  }

  for (let i = 0; i < generalPlans.length; i++) {
    const general = generalPlans[i];
    const kind = determinPlanKind({
      plan: general,
      workspaceInitialQuantity: workspacePlan?.initialQuantity ?? null,
    });

    //? if there are multiple plans with general types, we should skip the free plan if there are other plans available
    if (i !== generalPlans.length - 1 && kind === 'free') {
      continue;
    }

    return {
      general,
      workspace: workspacePlan,
      storage: storagePlan,
      users: getInvitesBasedOnPlan(workspacePlan?.initialQuantity ?? null),
    };
  }

  throw new Error('Plan is not found');
}

function determinPlanKind({
  plan,
  workspaceInitialQuantity,
}: {
  plan: PlanType;
  workspaceInitialQuantity: number | null;
}): PlanKindType {
  const { expireDate, fillDate } = plan;
  const startDate = parseISO(fillDate);
  const expiredDate = expireDate ? parseISO(expireDate) : null;
  const month = expiredDate
    ? differenceInCalendarMonths(expiredDate, startDate)
    : null;

  if (expireDate && month) {
    if (month >= 1 && month < 12)
      return workspaceInitialQuantity === 3
        ? GENIUS_SEARCH_PLAN_MONTHLY
        : GENIUS_TEAMS_PLAN_MONTHLY;
    if (month >= 12)
      return workspaceInitialQuantity === 3
        ? GENIUS_SEARCH_PLAN_YEARLY
        : GENIUS_TEAMS_PLAN_YEARLY;
  } else {
    return 'creditPack';
  }

  return 'free';
}

function getInvitesBasedOnPlan(workspaceInitialQuantity: number | null) {
  if (workspaceInitialQuantity === null) return 0;
  return workspaceInitialQuantity === 3 ? 20 : 100;
}

function convertToPercentage({
  quantity: remainingCredits,
  initialQuantity: totalCredits,
}: Pick<PlanType, 'quantity' | 'initialQuantity'>) {
  return (remainingCredits / totalCredits) * 100 > 0
    ? (remainingCredits / totalCredits) * 100
    : 0;
}
